import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import { FormInputWrapper } from "@insite/mobius/FormField/FormField";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { InputStyle, TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductBuyNowButton from "@tsesco/Components/ProductBuyNowButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@tsesco/Overrides/Components/ProductAddToListLink";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { getIsProductCompareFull, getProductCompareChecked } from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import siteMessage from "@insite/client-framework/SiteMessage";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return{
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: ownProps.product.id ? state.components.compareProductsDrawer.productsIds.includes(ownProps.product.id) : false,
    isProductCompareFull: getIsProductCompareFull(state),
}
};

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
    addProductToCompare,
    removeProductFromCompare,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    showAvailability?: boolean;
    showCompare?: boolean;
}

type Props = OwnProps & HasProductContext & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    availabilityItem?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    buttonStyles?: ButtonPresentationProps;
    productBuyNowButtonStyles?: ButtonPresentationProps;
    productBuyNowGridItem?: GridItemProps;
    callForPricing?: InjectableCss;
    compareCheckbox?: CheckboxPresentationProps;
    checkBoxWrapper?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin:0px;
            >a{
                padding: 4px 0px;
                margin-bottom: 4px;
                >span{
                font-weight: 500
                }
            }
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 10px;
                line-height: 1.125rem;
                align-items: flex-start;
                ${TypographyStyle} {
                    color: #000000;
                    font-size: 16px;
                    font-weight: 600;
                    font-family: 'Roboto-Medium';
                    // margin: 1rem 0rem;
                    margin: 0;
                }
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 14 },
        erpNumberValueText: {
            size: 14,
            css: css`
                margin-left: 2px;
            `,
        },
    },
    buttonStyles: {
        variant: "secondary",
        css: css`
            width: 100%;
            border: 1px solid ${getColor("primary")};
            min-height: 40px;
            ${TypographyStyle} {
                color: ${getColor("primary")};
            }
        `,
    },
    productBuyNowGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            display: grid;
        `,
    },
    productBuyNowButtonStyles: {
        css: css`
            min-height: 40px;
            border: 2px solid #E3931B;
            font-family: "Roboto-Medium" !important;
            font-weight: 600 !important;
            &:disabled {
                color: #fff !important;
                border-color: #cccccc !important;
                background: #cccccc !important;
                > span {
                    color: #fff !important;
                    font-weight: unset !important;
                }
            }
        `,
    },
    productPriceWrapper: {
        css: css`
            span[data-test-selector="productPrice_unitOfMeasureLabel"]{
                font-weight: 500;
                font-family: 'Roboto-Regular';
                text-transform: capitalize;
            }
            span[data-test-selector="productPrice_unitNetPrice"]{
                font-family: 'Roboto-Bold';
                font-size: 20px;
            }
            flex-grow: 1;
            /* margin-bottom: 6px; */
            section {
                padding: 0;
            }
            ${TypographyStyle} {
                color: ${getColor("secondary")};
                font-size: 1rem;
                font-family: "Roboto-Bold";
                line-height: 20px;
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 10px;
            margin-top: 10px;
            ${GridOffset} {
                max-width: 100% !important;
                width: 100% !important;
                margin: 0px !important;
                ${GridItemStyle}:first-child {
                    padding: 5px !important;
                }
            }
        `,
    },
    qtyGridItem: {
        width: 3,
        css: css`
            padding: 5px;
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                ${FormInputWrapper} {
                    ${InputStyle} {
                        border-radius: .5em;
                        background-color: #e8e9eb;
                    }
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        width: 9,
        css: css`
            ${ButtonWrapper} {
                padding: 0 !important;
            }
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
            min-height: 40px;
            background: #FFD812;
            border: 2px solid #FFA41C !important;
            font-size: 15px !important;
            font-weight: 600;
            text-transform: capitalize;
            color: #000000 !important;
            &:hover {
                background: #FFA41C !important;
            }
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 15, css: css`
                font-weight: 600;
                font-family: "Roboto-Medium";
                ` },
            css: css`
                margin-left: 6px;
            `,
        },

        mainContainer: {
            css: css`
                display: flex;
                align-items: center;
                padding: 5px 0px 0px 0px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                > ${GridOffset} {
                    display: flex;
                    flex-direction: column;
                    > ${GridItemStyle}:first-child {
                        padding: 0;
                    }
                    > ${GridItemStyle}:last-child {
                        padding: 0;
                        margin-top: 5px;
                    }
                }
            `,
        },
        messageGridItem: {
            width: 6,
            css: css`
                padding: 10px 15px;
                > div[color^="danger"] {
                    span[class^="IconWrapper"] {
                        display: none !important;
                    }
                }
                span[class^="IconWrapper"] {
                    min-width: 24px;
                }
                span[class^="TypographyStyle"] {
                    white-space: normal;
                }
                > div[class^="InventoryMessage"] {
                    >div[class^="sc"]{
                        align-items: flex-start;
                    }
                }
            `,
        },
    },
    productBrandStyles: {
        nameText: {
            size: 16,
            css: css`
                color: ${getColor("primary")};
            `,
        },
        nameLink: {
            typographyProps: {
                color: "secondary",
                size: "0.875rem",
                css: css`
                    text-transform: uppercase;
                `,
            },
        },
    },
    callForPricing: {
        css: css`
            padding-top: 20px;
            span {
                font-size: 16px;
                font-family: "Roboto-Bold";
            }
        `,
    },
    checkBoxWrapper:{
        css: css`
            text-align: center;
        `
    }
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    settingsCollection,
    showAvailability,
    productCompareChecked,
    isProductCompareFull,
    addProductToCompare,
    removeProductFromCompare,
    showCompare
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };
    const handleChanged = (value: React.SyntheticEvent) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };
    

    const productContext = {
        product,
        productInfo: productInfo!, // Assert that productInfo is not undefined
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const callForPricing = product.properties?.isCallForPricing?.toLowerCase() === "true";
    
    if (!product || !productInfo) {
        return null;
    }

    return (
        <>
            <ProductContext.Provider value={productContext}>
                {showImage && !showOverlayBadge && (
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                    />
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                        {...styles.badgeAreaWrapper}
                        ref={badgeAreaWrapperRef}
                        data-test-selector="productCarouselBadge"
                    >
                        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                
                {settingsCollection.productSettings.enableProductComparisons && (
                    <StyledWrapper {...styles.checkBoxWrapper}>
                        <Checkbox
                            {...styles.compareCheckbox}
                            checked={productCompareChecked}
                            onChange={(checked) => handleChanged(checked)}
                        >
                            {translate("Compare")}
                        </Checkbox>
                    </StyledWrapper>
                )}
                
                {showBrand && product.brand ? (
                    <StyledWrapper {...styles.productBrandWrapper}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                ) : (
                    <StyledWrapper></StyledWrapper>
                )}
                {showTitle && (
                    <ProductDescription
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productDescriptionStyles}
                    />
                )}
                {showPartNumbers && (
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        showCustomerName={false}
                        showManufacturerItem={false}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                )}
                {showTextBadges && textBadges && (
                    <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                )}
                {showImageBadges && imageBadges && (
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                )}
                {showPrice && !callForPricing && (
                    <StyledWrapper {...styles.productPriceWrapper}>
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice showLabel={false} product={productContext} />
                        {product.unitOfMeasures && (
                            <ProductUnitOfMeasureSelect
                                labelOverride=""
                                extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                id={`${carouselId}-uom`}
                            />
                        )}
                        <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                    </StyledWrapper>
                )}
                {settingsCollection.productSettings.showInventoryAvailability && showAvailability && (
                    <StyledWrapper {...styles.availabilityItem}>
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    </StyledWrapper>
                )}
                {showAddToCart && !callForPricing && (
                    <GridContainer {...styles.addToCartContainer}>
                        <GridItem {...styles.qtyGridItem}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${carouselId}-qty`}
                            />
                        </GridItem>
                        <GridItem {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButton
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </GridItem>
                        <GridItem {...styles.productBuyNowGridItem}>
                            <ProductBuyNowButton
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                extendedStyles={styles.productBuyNowButtonStyles}
                            />
                        </GridItem>
                    </GridContainer>
                )}
                {callForPricing && (
                    <StyledWrapper {...styles.callForPricing}>
                        <Link href="/request-quote">{translate("Call for Pricing")}</Link>
                    </StyledWrapper>
                )}
                {showAddToList && !callForPricing && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )}
            </ProductContext.Provider>
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct));
